import { useCallback, useEffect, useState } from 'react'

export type SelectionOption = {
  text: string
  value: string | number
}

export type SelectionOutput = {
  binder: SelectionBinder
  selectedOption?: SelectionOption
  reset: (option?: SelectionOption) => void
}

export interface SelectionBinder {
  selectedOption?: SelectionOption
  onSelect: (item?: SelectionOption) => void
}

export function useSelectionBinder(defaultOption?: SelectionOption): SelectionOutput {
  const [selectedOption, setSelectedOption] = useState<SelectionOption | undefined>(defaultOption)

  useEffect(() => {
    if (defaultOption) {
      setSelectedOption(defaultOption)
    }
  }, [defaultOption])

  function onSelect(option?: SelectionOption): void {
    setSelectedOption(option)
  }

  function reset(option?: SelectionOption): void {
    setSelectedOption(option ?? defaultOption)
  }

  const bind: SelectionBinder = { selectedOption: selectedOption, onSelect }

  return {
    binder: bind,
    selectedOption,
    reset,
  }
}

export type SelectionType = 'include' | 'exclude'

export type ExtendedSelectionOption = SelectionOption & {
  type?: SelectionType
}

export type MultiSelectionOutput = {
  binder: MultiSelectionBinder
  selectedOptions: SelectionOption[]
  excludedOptions: SelectionOption[]
  reset: (newSelectedOptions?: SelectionOption[], newExcludedOptions?: SelectionOption[]) => void
}

export interface MultiSelectionBinder {
  selectedOptions: SelectionOption[]
  excludedOptions: SelectionOption[]
  onToggle: (option: SelectionOption, type?: SelectionType) => void
  clearAll: () => void
}

export function useMultiSelectionBinder(): {
  binder: MultiSelectionBinder
  selectedOptions: SelectionOption[]
  excludedOptions: SelectionOption[]
  reset: (newSelectedOptions?: SelectionOption[], newExcludedOptions?: SelectionOption[]) => void
} {
  const [selectedOptions, setSelectedOptions] = useState<SelectionOption[]>([])
  const [excludedOptions, setExcludedOptions] = useState<SelectionOption[]>([])

  const onToggle = useCallback((option: SelectionOption, type: SelectionType = 'include') => {
    if (type === 'include') {
      setSelectedOptions((prev) => {
        const isAlreadySelected = prev.some((item) => item.value === option.value)
        if (isAlreadySelected) {
          return prev.filter((item) => item.value !== option.value)
        } else {
          setExcludedOptions((prevExcluded) => prevExcluded.filter((item) => item.value !== option.value))
          return [...prev, option]
        }
      })
    } else {
      setExcludedOptions((prev) => {
        const isAlreadyExcluded = prev.some((item) => item.value === option.value)
        if (isAlreadyExcluded) {
          return prev.filter((item) => item.value !== option.value)
        } else {
          setSelectedOptions((prevSelected) => prevSelected.filter((item) => item.value !== option.value))
          return [...prev, option]
        }
      })
    }
  }, [])

  const clearAll = useCallback(() => {
    setSelectedOptions([])
    setExcludedOptions([])
  }, [])

  const reset = useCallback((newSelectedOptions?: SelectionOption[], newExcludedOptions?: SelectionOption[]) => {
    setSelectedOptions(newSelectedOptions || [])
    setExcludedOptions(newExcludedOptions || [])
  }, [])

  const binder: MultiSelectionBinder = {
    selectedOptions,
    excludedOptions,
    onToggle,
    clearAll,
  }

  return {
    binder,
    selectedOptions,
    excludedOptions,
    reset,
  }
}
